import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import {
  renderViewersDesignSvg,
  type ViewersDesign,
  type ViewersParts,
} from "../../../../data/viewersDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/**
 * Design-store render strategy for the Viewers widget — sibling of the
 * FollowerGoal/SubGoal design renderers. Renders the chosen design's CARD SVG
 * (from the local catalog) as a single Pixi sprite stretched to the widget
 * bounds, re-rasterising the texture only when the displayed counts change.
 *
 * It does NOT poll: the parent `ViewersWidgetRenderer` already owns the 30s
 * `getViewers()` poll, so it just calls {@link update} with the resolved
 * total + per-platform counts. One instance per widget (kept in the parent's
 * Map).
 */
export class ViewersDesignRenderer {
  private container: Container | null = null;

  private sprite: Sprite | null = null;

  private destWidth = 1;

  private destHeight = 1;

  private renderScale = 1;

  /** Last counts key baked into the current texture. */
  private lastKey: string | null = null;

  /** Most recent counts pushed in (re-drawn once fonts finish decoding). */
  private lastParts: ViewersParts | null = null;

  /** Texture currently shown — destroyed before being replaced. */
  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  constructor(private readonly design: ViewersDesign) {}

  render(
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Preload the design's Google fonts, then force a redraw with the latest
    // counts so the real typeface (not a system fallback) is baked in.
    void ensureDesignFontsReady(this.design.fonts).then(() => {
      if (this.destroyed) return;
      this.lastKey = null;
      if (this.lastParts) void this.rerasterize(this.lastParts);
    });
  }

  /** Push fresh counts; re-rasterises only when the readout changes. */
  update(parts: ViewersParts): void {
    if (this.destroyed || !this.sprite) return;
    this.lastParts = parts;
    const key = this.keyOf(parts);
    if (key === this.lastKey) return;
    void this.rerasterize(parts);
  }

  private keyOf(p: ViewersParts): string {
    return `${p.total}|${p.twitch}|${p.youtube}|${p.kick}|${p.restream}|${p.facebook}`;
  }

  private async rerasterize(parts: ViewersParts): Promise<void> {
    if (this.destroyed || !this.sprite) return;

    // Claim this generation/key immediately so the equality guard in update()
    // doesn't fire a second raster for the same counts mid-decode.
    this.lastKey = this.keyOf(parts);
    const gen = ++this.generation;

    const svg = renderViewersDesignSvg(this.design, parts);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Assigning a texture in Pixi v8 resets the sprite to the texture's
      // intrinsic size — re-assert the widget bounds so the card always fills
      // `destination` regardless of renderScale.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      if (!this.destroyed && gen === this.generation) {
        this.lastKey = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.lastParts = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
